/**
 * CardPreview — visualização 3D do cartão de crédito enquanto o usuário
 * digita. Front mostra número (mascarado nos primeiros 12 dígitos quando
 * incompleto), holder, validade e brand mark. Back mostra a tarja
 * magnética, painel de assinatura e janela do CVV.
 *
 * Vira pro back quando `flipped=true` (use no foco do CVV input).
 *
 * Adaptado de stakeverse/src/components/wallet/add-payment-method-modal.tsx
 * (CardPreview + BrandMark) — base near-black com glow azul-acinzentado
 * sutil, chip EMV em SVG. Funciona em qualquer fundo.
 */
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { CardBrand } from '@/lib/card-brand';

interface CardPreviewProps {
  holder: string;
  /** Número completo (com ou sem espaços). Será mascarado se incompleto. */
  number: string;
  /** "MM/YY" pra exibir no campo Valid. */
  exp: string;
  brand: CardBrand;
  /** Valor digitado do CVV — mostrado no back quando `flipped`. */
  cvc: string;
  cvcLen: number;
  flipped: boolean;
}

export function CardPreview({
  holder,
  number,
  exp,
  brand,
  cvc,
  cvcLen,
  flipped,
}: CardPreviewProps) {
  const { t } = useTranslation();
  const digits = number.replace(/\D/g, '');
  // Para a exibição do número: mostra os 4 últimos quando o usuário já
  // digitou bastante; senão fica todo mascarado pra dar a sensação de
  // "preenchendo" sem expor demais.
  const last4 = digits.length >= 13 ? digits.slice(-4) : '';
  const grouped = ['••••', '••••', '••••', last4 || '••••'].join(' ');

  const cardBg =
    'linear-gradient(135deg,#07070a 0%,#121219 40%,#1a1a24 75%,#22222e 100%)';

  return (
    <div className="relative w-full aspect-[1.75/1] max-w-[420px] mx-auto" style={{ perspective: '1200px' }}>
      <motion.div
        className="absolute inset-0"
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
      >
        {/* ── FRONT ── */}
        <div
          className="absolute inset-0 rounded-xl overflow-hidden border border-white/10 p-4"
          style={{
            background: cardBg,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        >
          {/* glow azul-acinzentado sutil top-right */}
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at 85% 0%, rgba(90,90,110,0.5), transparent 55%)',
            }}
          />

          <div className="relative h-full grid grid-cols-[1fr_auto] gap-3 text-white">
            <div className="flex flex-col justify-between min-w-0">
              <div />
              <div className="font-mono text-[14px] sm:text-[16px] tracking-[0.22em] tabular-nums text-white/95 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                {grouped}
              </div>
              <div>
                <div className="text-[8px] uppercase tracking-[0.15em] opacity-55 font-semibold">
                  {t('billingUi:cardPreview.holderLabel')}
                </div>
                <div className="text-[11px] uppercase tracking-[0.1em] font-bold truncate max-w-[200px]">
                  {holder || 'NOME COMO NO CARTÃO'}
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-between items-end gap-2 shrink-0">
              <BrandMark brand={brand} />
              {/* EMV chip SVG */}
              <div className="w-10 h-8 rounded-[5px] relative bg-gradient-to-br from-[#FFD84A] to-[#D88E20] shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_2px_6px_rgba(0,0,0,0.45)]">
                <svg
                  viewBox="0 0 40 32"
                  className="absolute inset-0 w-full h-full"
                  preserveAspectRatio="none"
                >
                  <g stroke="rgba(0,0,0,0.32)" strokeWidth="1">
                    <line x1="3" y1="8" x2="16" y2="8" />
                    <line x1="3" y1="16" x2="16" y2="16" />
                    <line x1="3" y1="24" x2="16" y2="24" />
                    <line x1="24" y1="8" x2="37" y2="8" />
                    <line x1="24" y1="16" x2="37" y2="16" />
                    <line x1="24" y1="24" x2="37" y2="24" />
                  </g>
                  <rect x="16" y="4" width="8" height="24" rx="1.5" fill="rgba(0,0,0,0.28)" />
                </svg>
              </div>
              <div className="text-right">
                <div className="text-[8px] uppercase tracking-[0.15em] opacity-55 font-semibold">
                  {t('billingUi:cardPreview.validLabel')}
                </div>
                <div className="text-[11px] font-mono font-bold tabular-nums">
                  {exp || 'MM/AA'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── BACK ── */}
        <div
          className="absolute inset-0 rounded-xl overflow-hidden border border-white/10"
          style={{
            background: cardBg,
            transform: 'rotateY(180deg)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        >
          {/* magnetic stripe */}
          <div
            className="absolute left-0 right-0 top-[18%] h-[18%]"
            style={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.75), rgba(0,0,0,0.92) 50%, rgba(0,0,0,0.75))',
            }}
          />
          {/* signature + CVC */}
          <div className="absolute left-4 right-4 top-[48%] flex items-center gap-3">
            <div
              className="flex-1 h-8 rounded-[3px] relative overflow-hidden"
              style={{
                background:
                  'repeating-linear-gradient(45deg, #fff, #fff 4px, #e5e5e5 4px, #e5e5e5 8px)',
              }}
            >
              <span className="absolute left-2 top-1/2 -translate-y-1/2 font-mono text-[10px] italic text-black/70 tracking-wider truncate max-w-[80%]">
                {holder || 'Sua assinatura'}
              </span>
            </div>
            <div className="shrink-0 h-8 w-[64px] rounded-[3px] bg-white grid place-items-center shadow-[inset_0_1px_2px_rgba(0,0,0,0.25)]">
              <span className="font-mono font-bold text-[13px] text-black tabular-nums tracking-[0.18em]">
                {cvc ? cvc.padEnd(cvcLen, '•') : cvcLen === 4 ? '••••' : '•••'}
              </span>
            </div>
          </div>
          <div className="absolute left-4 right-4 bottom-3 flex items-end justify-between text-white/80">
            <span className="text-[8px] uppercase tracking-[0.12em] font-semibold leading-tight max-w-[60%]">
              {t('billingUi:cardPreview.authorizedSignature')}
            </span>
            <BrandMark brand={brand} size={26} />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function BrandMark({ brand, size = 32 }: { brand: CardBrand; size?: number }) {
  const { t } = useTranslation();
  if (brand === 'unknown') {
    return (
      <span className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-white/70">
        {t('billingUi:cardPreview.card')}
      </span>
    );
  }
  // Variantes brancas/alpha curadas pra contraste sobre o cartão preto
  return (
    <img
      src={`/brands/card/${brand}.png`}
      alt={brand}
      draggable={false}
      className="object-contain pointer-events-none select-none drop-shadow-[0_2px_6px_rgba(0,0,0,0.4)]"
      style={{ height: size, width: 'auto' }}
    />
  );
}
